// frontend/src/pages/admin/DietTemplates.jsx
import React, { useState, useEffect } from 'react'
import { Plus, Trash2, Save, X, Edit2, Check } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '../../services/api'

export default function DietTemplates() {
    const [templates, setTemplates] = useState([])
    const [selectedTemplate, setSelectedTemplate] = useState(null)
    const [isCreating, setIsCreating] = useState(false)
    const [newTemplateName, setNewTemplateName] = useState('')
    // Nouvel état pour gérer l'item en cours d'édition
    const [editingItemId, setEditingItemId] = useState(null)
    const [editItemData, setEditItemData] = useState({})

    // États pour ajouter un item au template sélectionné
    const [newItem, setNewItem] = useState({ meal_time: 'breakfast', food_name: '', quantity: '', notes: '' })

    // --- AXES DYNAMIQUES ---
    const[predefinedAxes, setPredefinedAxes] = useState([])
    const[newAxeText, setNewAxeText] = useState('')

    // --- CIQUAL ---
    const [suggestions, setSuggestions] = useState([])
    const [showSuggestions, setShowSuggestions] = useState(false)
    // --------------------


    useEffect(() => { loadTemplates() }, [])

    const loadTemplates = async () => {
        try {
            const [tplRes, settingsRes] = await Promise.all([
                api.get('/api/diet-plans/templates'),
                api.get('/api/settings/')
            ]);
            setTemplates(tplRes.data)
            
            // Récupération des axes dans les paramètres
            const axesSetting = settingsRes.data.find(s => s.setting_key === 'diet_predefined_axes')
            if (axesSetting && axesSetting.setting_value) {
                setPredefinedAxes(JSON.parse(axesSetting.setting_value))
            } else {
                // Valeurs par défaut si vide
                setPredefinedAxes(["Bien mastiquer (20 min par repas)", "S'hydrater (1.5L d'eau/jour)"])
            }
        } catch (e) { console.error(e) }
    }
    
    // Fonction pour sauvegarder les axes
    const saveAxesToSettings = async (axesArray) => {
        try {
            await api.patch('/api/settings/diet_predefined_axes', { value: JSON.stringify(axesArray) })
        } catch (e) { toast.error("Erreur de sauvegarde des axes") }
    }

    const handleAddAxe = () => {
        if (!newAxeText.trim()) return;
        const updatedAxes = [...predefinedAxes, newAxeText.trim()];
        setPredefinedAxes(updatedAxes);
        saveAxesToSettings(updatedAxes);
        setNewAxeText('');
        toast.success("Axe ajouté");
    }

    const handleRemoveAxe = (indexToRemove) => {
        if(!confirm("Supprimer cet axe de la bibliothèque ?")) return;
        const updatedAxes = predefinedAxes.filter((_, idx) => idx !== indexToRemove);
        setPredefinedAxes(updatedAxes);
        saveAxesToSettings(updatedAxes);
    }

    const handleCreateTemplate = async () => {
        if(!newTemplateName) return
        try {
            const res = await api.post('/api/diet-plans/templates', { name: newTemplateName })
            setTemplates([...templates, res.data])
            setIsCreating(false)
            setNewTemplateName('')
            toast.success("Modèle créé")
        } catch(e) { toast.error("Erreur création") }
    }

    const handleDeleteTemplate = async (id) => {
        if(!confirm("Supprimer ce modèle ?")) return
        await api.delete(`/api/diet-plans/templates/${id}`)
        setTemplates(templates.filter(t => t.id !== id))
        if(selectedTemplate?.id === id) setSelectedTemplate(null)
    }

    const handleAddItem = async () => {
        if(!selectedTemplate || !newItem.food_name) return
        try {
            const res = await api.post(`/api/diet-plans/templates/${selectedTemplate.id}/items`, newItem)
            // Mise à jour locale
            const updated = { ...selectedTemplate, items: [...selectedTemplate.items, res.data] }
            setSelectedTemplate(updated)
            setTemplates(templates.map(t => t.id === updated.id ? updated : t))
            setNewItem({ ...newItem, food_name: '', quantity: '', notes: '' }) // Reset champs sauf meal_time
        } catch(e) { toast.error("Erreur ajout item") }
    }

    // Fonction pour démarrer l'édition
    const startEditItem = (item) => {
        setEditingItemId(item.id)
        setEditItemData({ ...item })
    }

    // Fonction pour sauvegarder
    const handleUpdateItem = async () => {
        try {
            // Appel API (Note bien l'URL correspondante au backend créé ci-dessus)
            const res = await api.patch(`/api/diet-plans/template-items/${editingItemId}`, {
                food_name: editItemData.food_name,
                quantity: editItemData.quantity,
                notes: editItemData.notes,
                weight_g: editItemData.weight_g,
                ciqual_code: editItemData.ciqual_code,
            })
            
            // Mise à jour locale
            const updatedItems = selectedTemplate.items.map(i => i.id === editingItemId ? res.data : i)
            const updatedTemplate = { ...selectedTemplate, items: updatedItems }
            
            setSelectedTemplate(updatedTemplate)
            setTemplates(templates.map(t => t.id === updatedTemplate.id ? updatedTemplate : t))
            
            setEditingItemId(null)
            toast.success("Modifié !")
        } catch (e) {
            toast.error("Erreur modification")
        }
    }

    const handleDeleteItem = async (itemId) => {
        if(!confirm("Supprimer cet aliment ?")) return
        try {
            await api.delete(`/api/diet-plans/template-items/${itemId}`)
            const updatedItems = selectedTemplate.items.filter(i => i.id !== itemId)
            const updatedTemplate = { ...selectedTemplate, items: updatedItems }
            setSelectedTemplate(updatedTemplate)
            setTemplates(templates.map(t => t.id === updatedTemplate.id ? updatedTemplate : t))
            toast.success("Supprimé !")
        } catch(e) { toast.error("Erreur suppression") }
    }


    // --- FONCTION RECHERCHE CIQUAL (Unifiée) ---
    const handleSearchCiqual = async (text) => {
        // 1. Mise à jour de l'affichage (Input)
        if (editingItemId) {
             // Mode Édition
            setEditItemData(prev => ({ ...prev,
                 food_name: text,
                 ciqual_code: null,   // ← reset si saisie libre
                 ciqual_data: null
            }))
        } else {
             // Mode Création
            setNewItem(prev => ({ ...prev,
                 food_name: text,
                 ciqual_code: null,   // ← reset si saisie libre
                 ciqual_data: null
            }))
        }
        
        // 2. Appel API Ciqual (si > 2 caractères)
        if (text.length < 2) {
            setSuggestions([])
            setShowSuggestions(false)
            return
        }

        try {
            const res = await api.get(`/api/ciqual/search?q=${encodeURIComponent(text)}`)
            setSuggestions(res.data)
            setShowSuggestions(true)
        } catch (error) {
            console.error("Erreur Ciqual", error)
        }
    }

    // --- SÉLECTION D'UN ITEM ---
    const selectCiqualItem = (item) => {
        // On ne met plus les calories dans les notes texte, c'est géré nativement maintenant !
        
        if (editingItemId) {
            setEditItemData(prev => ({ 
                ...prev, 
                food_name: item.nom_fr,
                ciqual_code: item.code, // <--- IMPORTANT : On stocke le lien
                // On garde les données brutes pour l'affichage immédiat sans recharger
                ciqual_data: item 
            }))
        } else {
            setNewItem(prev => ({ 
                ...prev, 
                food_name: item.nom_fr,
                ciqual_code: item.code, // <--- IMPORTANT
                ciqual_data: item 
            }))
        }
        setShowSuggestions(false)
    }


    // --- CALCULATEURS ---
    
    // Calcule les macros pour UN item
    const getItemMacros = (item) => {
        // Il faut un poids ET des données Ciqual associées
        if (!item.weight_g || !item.ciqual_data) return null;
        
        const ratio = item.weight_g / 100;
        return {
            kcal: item.ciqual_data.energie_kcal * ratio,
            prot: item.ciqual_data.proteines * ratio,
            gluc: item.ciqual_data.glucides * ratio,
            lip: item.ciqual_data.lipides * ratio
        };
    };

    // Calcule le total pour un REPAS (Matin, Midi...)
    const getMealTotals = (mealKey) => {
        if (!selectedTemplate) return null;
        
        const items = selectedTemplate.items.filter(i => i.meal_time === mealKey);
        const totals = { kcal: 0, prot: 0, gluc: 0, lip: 0 };
        
        items.forEach(item => {
            const macros = getItemMacros(item);
            if (macros) {
                totals.kcal += macros.kcal;
                totals.prot += macros.prot;
                totals.gluc += macros.gluc;
                totals.lip += macros.lip;
            }
        });
        
        return totals;
    };

    // Calcule le total JOURNÉE
    const getDailyTotal = () => {
        const meals = ['breakfast', 'lunch', 'snack', 'dinner'];
        const total = { kcal: 0, prot: 0, gluc: 0, lip: 0 };
        
        meals.forEach(meal => {
            const mTotal = getMealTotals(meal);
            total.kcal += mTotal.kcal;
            total.prot += mTotal.prot;
            total.gluc += mTotal.gluc;
            total.lip += mTotal.lip;
        });
        return total;
    };



    return (
        <div className="h-[calc(100vh-100px)] flex gap-6">
            
            {/* COLONNE GAUCHE : LISTE DES MODÈLES */}
            <div className="w-1/3 bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center">
                    <h2 className="font-serif font-bold text-gray-800">Modèles</h2>
                    <button onClick={() => setIsCreating(true)} className="p-2 bg-sage-100 text-sage-700 rounded-lg hover:bg-sage-200">
                        <Plus className="w-5 h-5"/>
                    </button>
                </div>
                
                {isCreating && (
                    <div className="p-3 bg-sage-50 border-b border-gray-100">
                        <input 
                            autoFocus
                            placeholder="Nom du modèle..." 
                            className="w-full mb-2 text-sm"
                            value={newTemplateName}
                            onChange={e => setNewTemplateName(e.target.value)}
                        />
                        <div className="flex gap-2">
                            <button onClick={handleCreateTemplate} className="btn btn-primary py-1 text-xs flex-1">Créer</button>
                            <button onClick={() => setIsCreating(false)} className="btn bg-white border border-gray-300 py-1 text-xs">Annuler</button>
                        </div>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto p-2 space-y-2">
                    {templates.map(tpl => (
                        <div 
                            key={tpl.id}
                            onClick={() => setSelectedTemplate(tpl)}
                            className={`p-3 rounded-lg cursor-pointer border flex justify-between items-center ${selectedTemplate?.id === tpl.id ? 'bg-sage-50 border-sage-300' : 'bg-white border-gray-100 hover:border-gray-300'}`}
                        >
                            <span className="font-medium text-gray-700">{tpl.name}</span>
                            <button onClick={(e) => { e.stopPropagation(); handleDeleteTemplate(tpl.id); }} className="text-gray-400 hover:text-red-500">
                                <Trash2 className="w-4 h-4"/>
                            </button>
                        </div>
                    ))}
                </div>
                
                {/* NOUVEAU : GESTION DES AXES */}
                <div className="p-4 border-t border-gray-200 bg-gray-50 mt-auto">
                    <h3 className="font-bold text-gray-700 text-xs uppercase mb-3">🎯 Dictionnaire des Axes</h3>
                    <div className="flex gap-2 mb-3">
                        <input 
                            value={newAxeText}
                            onChange={(e) => setNewAxeText(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddAxe()}
                            placeholder="Nouvel axe de travail..."
                            className="w-full text-xs py-1.5"
                        />
                        <button onClick={handleAddAxe} className="btn btn-primary px-3 py-1.5 text-xs"><Plus className="w-4 h-4"/></button>
                    </div>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                        {predefinedAxes.map((axe, idx) => (
                            <div key={idx} className="flex justify-between items-center bg-white border border-gray-200 px-2 py-1.5 rounded-lg group text-xs">
                                <span className="text-gray-600 truncate">{axe}</span>
                                <button onClick={() => handleRemoveAxe(idx)} className="text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Trash2 className="w-3.5 h-3.5"/>
                                </button>
                            </div>
                        ))}
                        {predefinedAxes.length === 0 && <p className="text-[10px] text-gray-400 italic">Aucun axe configuré.</p>}
                    </div>
                </div>
            </div>

            {/* COLONNE DROITE : DETAILS ET ÉDITION */}
            <div className="flex-1 bg-white rounded-xl shadow-sm border border-gray-200 flex flex-col">
                {selectedTemplate ? (
                    <>
                        <div className="p-4 border-b border-gray-100 bg-gray-50 rounded-t-xl">
                            <h2 className="font-serif font-bold text-xl text-gray-800">{selectedTemplate.name}</h2>
                            <p className="text-xs text-gray-500">Édition du contenu type</p>
                        </div>
                        {/* RÉSUMÉ NUTRITIONNEL DU PLAN */}
                        <div className="px-6 py-2 bg-sage-50 border-b border-sage-100 flex gap-6 text-sm">
                            <span className="font-bold text-sage-800">Total Journée :</span>
                            <span className="font-bold">{Math.round(getDailyTotal().kcal)} kcal</span>
                            <div className="flex gap-3 text-gray-600 text-xs items-center">
                                <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded">P: {Math.round(getDailyTotal().prot)}g</span>
                                <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded">G: {Math.round(getDailyTotal().gluc)}g</span>
                                <span className="bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">L: {Math.round(getDailyTotal().lip)}g</span>
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6">
                            {['breakfast', 'lunch', 'snack', 'dinner'].map(meal => {
                                const mealTotal = getMealTotals(meal);
                                return (
                                <div key={meal} className="mb-6">
                                    <div className="flex justify-between items-end border-b border-sage-100 pb-1 mb-2">
                                        <h3 className="font-bold text-sage-700 uppercase text-xs">
                                            {{breakfast:'Petit-déjeuner', lunch:'Déjeuner', snack:'Collation', dinner:'Dîner'}[meal]}
                                        </h3>
                                        <span className="text-xs font-bold text-gray-400">
                                            {Math.round(mealTotal.kcal)} kcal
                                        </span>
                                    </div>
                                    
                                    <ul className="space-y-2">
                                        {selectedTemplate.items.filter(i => i.meal_time === meal).map(item => {
                                            const macros = getItemMacros(item);
                                            return (
                                            <li key={item.id} className="text-sm group flex items-start gap-2 py-1">
                                                
                                                {editingItemId === item.id ? (
                                                    // --- MODE ÉDITION AVEC POIDS ---
                                                    <div className="flex-1 flex gap-2 items-center animate-in fade-in flex-wrap">
                                                        <div className="relative flex-1 min-w-[150px]">
                                                            {/* INPUT ALIMENT (Comme avant avec handleSearchCiqual) */}
                                                            <input 
                                                                className="w-full px-2 py-1 text-sm border-sage-300 rounded focus:ring-2 focus:ring-sage-200 focus:border-sage-500"
                                                                value={editItemData.food_name}
                                                                // On branche notre fonction intelligente
                                                                onChange={(e) => handleSearchCiqual(e.target.value)}
                                                                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                                                autoFocus
                                                            />
                                                            
                                                            {/* LA LISTE DÉROULANTE (Identique à celle du bas) */}
                                                            {showSuggestions && suggestions.length > 0 && (
                                                                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto z-50">
                                                                    {suggestions.map((s) => (
                                                                        <div 
                                                                            key={s.code}
                                                                            // Utilise onMouseDown pour prendre le clic avant le onBlur
                                                                            onMouseDown={() => selectCiqualItem(s)}
                                                                            className="px-3 py-2 hover:bg-sage-50 cursor-pointer text-xs border-b border-gray-50 last:border-0"
                                                                        >
                                                                            <div className="font-bold text-gray-700">{s.nom_fr}</div>
                                                                            <div className="text-gray-400">{Math.round(s.energie_kcal)} kcal</div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {/* INPUT POIDS (Nouveau) */}
                                                        <div className="relative w-20">
                                                            <input 
                                                                type="number"
                                                                className="w-full px-2 py-1 text-sm border-sage-300 rounded text-right pr-6"
                                                                placeholder="g"
                                                                value={editItemData.weight_g || '0'}
                                                                onChange={e => setEditItemData({...editItemData, weight_g: e.target.value})}
                                                            />
                                                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-400">g</span>
                                                        </div>

                                                        {/* INPUT TEXTE LIBRE (Ex: 2 tranches) */}
                                                        <input 
                                                            className="w-24 px-2 py-1 text-sm border-sage-300 rounded"
                                                            placeholder="Txt (ex: 2 pcs)"
                                                            value={editItemData.quantity || ''}
                                                            onChange={e => setEditItemData({...editItemData, quantity: e.target.value})}
                                                        />
                                                        <button onClick={handleUpdateItem} className="p-1 text-green-600 hover:bg-green-50 rounded">
                                                            <Check className="w-4 h-4" />
                                                        </button>
                                                        <button onClick={() => setEditingItemId(null)} className="p-1 text-gray-400 hover:bg-gray-50 rounded">
                                                            <X className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    // --- MODE LECTURE ---
                                                    <div className="flex-1">
                                                        <div className="flex justify-between items-start">
                                                            <span className="font-medium">
                                                                • {item.food_name}
                                                                {/* Affichage intelligent du poids/quantité */}
                                                                {(item.quantity || item.weight_g) && (
                                                                    <span className="text-gray-500 text-xs ml-1 font-normal">
                                                                        ({item.quantity ? item.quantity : ''}
                                                                         {item.quantity && item.weight_g ? ' / ' : ''}
                                                                         {item.weight_g ? `${item.weight_g}g` : ''})
                                                                    </span>
                                                                )}
                                                            </span>
                                                            
                                                            {/* Affichage Macros Item */}
                                                            {macros && (
                                                                <span className="text-[10px] text-sage-600 bg-sage-50 px-1.5 rounded ml-2 whitespace-nowrap">
                                                                    {Math.round(macros.kcal)} kcal
                                                                </span>
                                                            )}
                                                        </div>
                                                        {item.notes && <div className="text-gray-400 italic text-xs ml-3">- {item.notes}</div>}
                                                    </div>
                                                )}

                                                        {/* Boutons d'action (visibles au survol) */}
                                                        <div className="ml-auto flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <button 
                                                                onClick={() => startEditItem(item)} 
                                                                className="p-1 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded"
                                                                title="Modifier"
                                                            >
                                                                <Edit2 className="w-3 h-3"/>
                                                            </button>
                                                            <button 
                                                                onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id); }} 
                                                                className="p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                                                                title="Supprimer"
                                                            >
                                                                <Trash2 className="w-3 h-3"/>
                                                            </button>
                                                        </div>
                                            </li>
                                        )})}
                                    </ul>
                                </div>
                            )})}
                        </div>

                        {/* FORMULAIRE AJOUT RAPIDE */}
                        <div className="p-4 border-t border-gray-100 bg-gray-50 rounded-b-xl">
                            <div className="flex gap-2">
                                <select 
                                    className="w-32 text-sm"
                                    value={newItem.meal_time}
                                    onChange={e => setNewItem({...newItem, meal_time: e.target.value})}
                                >
                                    <option value="breakfast">Matin</option>
                                    <option value="lunch">Midi</option>
                                    <option value="snack">Goûter</option>
                                    <option value="dinner">Soir</option>
                                </select>
                                {/* INPUT CIQUAL */}
                                <div className="relative flex-1">
                                    <input 
                                        placeholder="Aliment..."
                                        className="w-full text-sm"
                                        value={newItem.food_name}
                                        onChange={(e) => handleSearchCiqual(e.target.value)}
                                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                    />
                                    {/* LISTE DEROULANTE */}
                                    {showSuggestions && suggestions.length > 0 && !editingItemId && (
                                        <div className="absolute bottom-full left-0 right-0 mb-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto z-50">
                                            {suggestions.map((s) => (
                                                <div 
                                                    key={s.code}
                                                    onMouseDown={() => selectCiqualItem(s)} // onMouseDown agit avant onBlur
                                                    className="px-3 py-2 hover:bg-sage-50 cursor-pointer text-xs border-b border-gray-50"
                                                >
                                                    <div className="font-bold">{s.nom_fr}</div>
                                                    <div className="text-gray-400">{Math.round(s.energie_kcal)} kcal</div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <input 
                                    type="number"
                                    placeholder="g"
                                    className="w-16 text-sm"
                                    value={newItem.weight_g || '0'}
                                    onChange={e => setNewItem({...newItem, weight_g: e.target.value})}
                                />
                                <input 
                                    placeholder="Note"
                                    className="w-20 text-sm"
                                    value={newItem.quantity}
                                    onChange={e => setNewItem({...newItem, quantity: e.target.value})}
                                />
                                <button onClick={handleAddItem} className="btn btn-primary py-1 px-4">
                                    Ajouter
                                </button>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex-1 flex items-center justify-center text-gray-400">
                        Sélectionnez un modèle pour l'éditer
                    </div>
                )}
            </div>
        </div>
    )
}